import React, { useState } from 'react';
import { faCalendar, faFilter, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from './Button';
import Input from './Input';

export type DateRange = {
  startDate: string | null;
  endDate: string | null;
};

export type FilterPreset = 'current_week' | 'last_week' | 'current_month' | 'custom' | null;

interface DateRangeFilterProps {
  dateRange: DateRange;
  activeFilter: FilterPreset;
  onDateRangeChange: (dateRange: DateRange, preset: FilterPreset) => void;
  onClearFilter: () => void;
}

const getWeekStart = (date: Date): Date => {
  const day = date.getDay();
  const diff = date.getDate() - day;
  return new Date(date.setDate(diff));
};

const getWeekEnd = (date: Date): Date => {
  const weekStart = getWeekStart(new Date(date));
  return new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
};

const formatDateForInput = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateRange,
  activeFilter,
  onDateRangeChange,
  onClearFilter
}) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(dateRange.startDate || '');
  const [customEndDate, setCustomEndDate] = useState(dateRange.endDate || '');

  const handlePresetFilter = (preset: FilterPreset) => {
    const today = new Date();
    let startDate: string | null = null;
    let endDate: string | null = null;

    switch (preset) {
      case 'current_week':
        startDate = formatDateForInput(getWeekStart(new Date(today)));
        endDate = formatDateForInput(getWeekEnd(new Date(today)));
        break;
      case 'last_week':
        const lastWeekStart = getWeekStart(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
        const lastWeekEnd = getWeekEnd(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
        startDate = formatDateForInput(lastWeekStart);
        endDate = formatDateForInput(lastWeekEnd);
        break;
      case 'current_month':
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        startDate = formatDateForInput(firstDayOfMonth);
        endDate = formatDateForInput(lastDayOfMonth);
        break;
      case 'custom':
        setShowCustom(true);
        return;
      default:
        break;
    }

    setShowCustom(false);
    onDateRangeChange({ startDate, endDate }, preset);
  };

  const handleCustomFilter = () => {
    if (customStartDate && customEndDate) {
      onDateRangeChange({ startDate: customStartDate, endDate: customEndDate }, 'custom');
      setShowCustom(false);
    }
  };

  const handleClearFilter = () => {
    setShowCustom(false);
    setCustomStartDate('');
    setCustomEndDate('');
    onClearFilter();
  };

  const isFilterActive = activeFilter !== null;

  return (
    <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <FontAwesomeIcon icon={faCalendar} className="mr-2 w-5 h-5 text-gray-600" />
          Filtrar por Data
        </h3>
        {isFilterActive && (
          <Button onClick={handleClearFilter} variant="outline" size="sm" leftIcon={faTimesCircle}>
            Limpar Filtro
          </Button>
        )}
      </div>

      {!showCustom ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button
            onClick={() => handlePresetFilter('current_week')}
            variant={activeFilter === 'current_week' ? 'primary' : 'outline'}
            size="sm"
            fullWidth
          >
            Esta Semana
          </Button>
          <Button
            onClick={() => handlePresetFilter('last_week')}
            variant={activeFilter === 'last_week' ? 'primary' : 'outline'}
            size="sm"
            fullWidth
          >
            Semana Passada
          </Button>
          <Button
            onClick={() => handlePresetFilter('current_month')}
            variant={activeFilter === 'current_month' ? 'primary' : 'outline'}
            size="sm"
            fullWidth
          >
            Este Mês
          </Button>
          <Button
            onClick={() => handlePresetFilter('custom')}
            variant={activeFilter === 'custom' ? 'primary' : 'outline'}
            size="sm"
            fullWidth
            leftIcon={faFilter}
          >
            Filtro
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Data Inicial"
              value={customStartDate}
              onChange={e => setCustomStartDate(e.target.value)}
              placeholder="Data inicial"
            />
            <Input
              type="date"
              label="Data Final"
              value={customEndDate}
              onChange={e => setCustomEndDate(e.target.value)}
              placeholder="Data final"
            />
          </div>
          <div className="flex items-center space-x-3">
            <Button
              onClick={handleCustomFilter}
              variant="primary"
              size="sm"
              disabled={!customStartDate || !customEndDate}
            >
              Aplicar Filtro
            </Button>
            <Button onClick={() => setShowCustom(false)} variant="outline" size="sm">
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {isFilterActive && (
        <div className="mt-4 p-3 bg-blue-50 rounded-md">
          <p className="text-sm text-blue-700">
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Filtro ativo: {activeFilter === 'current_week' && 'Esta Semana'}
            {activeFilter === 'last_week' && 'Semana Passada'}
            {activeFilter === 'current_month' && 'Este Mês'}
            {activeFilter === 'custom' &&
              dateRange.startDate &&
              dateRange.endDate &&
              `${formatDateForDisplay(dateRange.startDate)} até ${formatDateForDisplay(dateRange.endDate)}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
